import { useEffect, useState } from 'react'
import { useWeb3 } from '../contexts/Web3Context'
import { PLSX_SIGNATURE_MESSAGE } from '@bridge-sponsor/types'

export default function ClaimGasFlow() {
  const { address, isConnected, isWrongChain, walletClient } = useWeb3()
  const [eligible, setEligible] = useState(false)
  const [claimStatus, setClaimStatus] = useState('')
  const [sponsorTxHash, setSponsorTxHash] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Reset state when address changes
  useEffect(() => {
    setEligible(false)
    setClaimStatus('')
    setSponsorTxHash('')
  }, [address])

  const checkEligibility = async () => {
    if (!address) return

    try {
      const res = await fetch('/api/plsx/check', {
        method: 'POST',
        body: JSON.stringify({ address }),
        headers: { 'content-type': 'application/json' },
      })
      const result = await res.json()
      setEligible(result.eligible)
    } catch (error) {
      console.error('Error checking eligibility:', error)
    }
  }

  const checkStatus = async () => {
    if (!address) return

    try {
      const res = await fetch('/api/plsx/status', {
        method: 'POST',
        body: JSON.stringify({ address }),
        headers: { 'content-type': 'application/json' },
      })
      const result = await res.json()
      setClaimStatus(result.status)
      if (result.sponsorTxHash) {
        setSponsorTxHash(result.sponsorTxHash)
      }
    } catch (error) {
      console.error('Error checking status:', error)
    }
  }

  // Check eligibility and initial status when wallet connects
  useEffect(() => {
    if (!isConnected || isWrongChain || !address) return

    checkEligibility()
    checkStatus()
  }, [isConnected, isWrongChain, address])

  // Poll status every 5 seconds when connected
  useEffect(() => {
    if (!isConnected || isWrongChain || !address) return

    const interval = setInterval(checkStatus, 5000)
    return () => clearInterval(interval)
  }, [isConnected, isWrongChain, address])

  const claimGas = async () => {
    if (!walletClient || !address) return

    setIsLoading(true)
    try {
      const signature = await walletClient.signMessage({
        message: PLSX_SIGNATURE_MESSAGE,
      })

      const res = await fetch('/api/plsx/signed', {
        method: 'POST',
        body: JSON.stringify({ address, signature }),
        headers: { 'content-type': 'application/json' },
      })
      const result = await res.json()
      console.log('Claim result:', result)

      await checkStatus()
    } catch (error) {
      console.error('Error claiming gas:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isConnected || isWrongChain || !address) return null

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4">Claim Gas Sponsorship</h2>

      {sponsorTxHash ? (
        <div className="flex items-center gap-2">
          <span className="text-green-400 text-sm">Sponsorship sent!</span>
          <a
            href={`https://otter.pulsechain.com/tx/${sponsorTxHash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300 text-sm underline"
          >
            View transaction
          </a>
        </div>
      ) : eligible ? (
        <div className="flex flex-col gap-2">
          <button
            onClick={claimGas}
            disabled={isLoading}
            className="py-3 px-4 text-sm font-medium text-white bg-pulse-gradient rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Claiming...' : `Claim Gas${claimStatus === 'pending' ? ' ...' : ''}`}
          </button>
          {claimStatus === 'pending' && (
            <p className="text-sm text-slate-400">Your claim is pending...</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400">Not eligible for gas sponsorship.</p>
      )}
    </div>
  )
}
